import React, { useMemo, useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Box,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import LiveIndicator from '../shared/LiveIndicator';
import { useHistory } from 'react-router-dom';
import Tooltip from '@material-ui/core/Tooltip';
import { getIdleDiagnosticsPresentation } from './idleDiagnosticsView';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 600
  },
  table: {
    minWidth: 650
  },
  healthChip: {
    fontWeight: 500
  },
  stateChip: {
    fontWeight: 500,
    textTransform: 'uppercase',
    fontSize: '0.75rem'
  },
  metricsBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5)
  },
  metricLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary
  },
  progressBar: {
    height: 6,
    borderRadius: 3
  },
  noData: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  regionSelect: {
    minWidth: 180
  },
  regionChip: {
    fontFamily: 'monospace',
    fontSize: '0.7rem'
  },
  pluginVersions: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
    marginTop: theme.spacing(0.5)
  }
}));

const AgentList = ({ agents = [] }) => {
  const classes = useStyles();
  const history = useHistory();
  const [regionFilter, setRegionFilter] = useState('all');

  const availableRegions = useMemo(() => {
    const set = new Set();
    for (const a of agents) {
      if (a.awsRegion) set.add(a.awsRegion);
    }
    return Array.from(set).sort();
  }, [agents]);

  const filteredAgents = useMemo(() => {
    if (regionFilter === 'all') return agents;
    return agents.filter((a) => a.awsRegion === regionFilter);
  }, [agents, regionFilter]);

  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'unhealthy':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const getHealthIcon = (health) => {
    switch (health) {
      case 'healthy':
        return <CheckCircleIcon style={{ fontSize: 16 }} />;
      case 'degraded':
        return <WarningIcon style={{ fontSize: 16 }} />;
      case 'unhealthy':
        return <ErrorIcon style={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };
  
  const getStateColor = (state) => {
    switch (state) {
      case 'IDLE':
        return 'primary';
      case 'PLAYING':
      case 'STARTED':
        return 'success';
      case 'PREPARING':
        return 'default';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  };
  
  const getProgressColor = (value) => {
    if (value > 80) return 'error';
    if (value > 60) return 'warning';
    return 'primary';
  };

  const renderPluginDiagnostics = (agent) => {
    const presentation = getIdleDiagnosticsPresentation(agent.idleDiagnostics);
    return (
      <Box>
        <Tooltip title={presentation.tooltip} placement="top">
          <Chip
            label={presentation.label}
            color={presentation.color}
            size="small"
            className={classes.healthChip}
          />
        </Tooltip>
        {(agent.idleDiagnostics?.metamodVersion ||
          agent.idleDiagnostics?.counterStrikeSharpVersion) && (
          <Box className={classes.pluginVersions}>
            {agent.idleDiagnostics.metamodVersion && (
              <div>MM {agent.idleDiagnostics.metamodVersion}</div>
            )}
            {agent.idleDiagnostics.counterStrikeSharpVersion && (
              <div>CSS {agent.idleDiagnostics.counterStrikeSharpVersion}</div>
            )}
          </Box>
        )}
      </Box>
    );
  };
  
  if (agents.length === 0) {
    return (
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.title}>
          Game Servers
        </Typography>
        <Box className={classes.noData}>
          <Typography>No agents connected</Typography>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        Game Servers ({filteredAgents.length}{regionFilter !== 'all' ? ` / ${agents.length}` : ''})
      </Typography>

      {availableRegions.length > 1 && (
        <Box className={classes.filterBar}>
          <FormControl variant="outlined" size="small" className={classes.regionSelect}>
            <InputLabel>Region</InputLabel>
            <Select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              label="Region"
            >
              <MenuItem value="all">All Regions</MenuItem>
              {availableRegions.map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}
      
      <TableContainer>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell>IP Address</TableCell>
              <TableCell>Region</TableCell>
              <TableCell>Agent Version</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Plugins</TableCell>
              <TableCell>Health</TableCell>
              <TableCell>CPU Usage</TableCell>
              <TableCell>Memory Usage</TableCell>
              <TableCell>CS2 Status</TableCell>
              <TableCell>Match ID</TableCell>
              <TableCell>Last Seen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAgents.map((agent) => {
              const lastSeenSeconds = agent.lastSeen 
                ? Math.floor((Date.now() - agent.lastSeen) / 1000)
                : null;
              
              const isOnline = lastSeenSeconds !== null && lastSeenSeconds < 60;
              
              return (
                <TableRow
                  key={agent.ip}
                  hover
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    const agentKey = agent.instanceId || agent.ip;
                    history.push(`/agent/${encodeURIComponent(agentKey)}/details`);
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <LiveIndicator live={isOnline} showText={false} />
                      <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                        {agent.ip}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" className={classes.regionChip}>
                      {agent.awsRegion || '-'}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {agent.version || agent.agentVersion || '-'}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      label={agent.state || 'UNKNOWN'}
                      color={getStateColor(agent.state)}
                      size="small"
                      className={classes.stateChip}
                    />
                  </TableCell>

                  <TableCell>
                    {renderPluginDiagnostics(agent)}
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={getHealthIcon(agent.health)}
                      label={agent.health || 'unknown'}
                      color={getHealthColor(agent.health)}
                      size="small"
                      className={classes.healthChip}
                    />
                  </TableCell>
                  
                  <TableCell>
                    <Box className={classes.metricsBox}>
                      <Typography className={classes.metricLabel}>
                        {agent.cpuUsage ? `${agent.cpuUsage.toFixed(1)}%` : 'N/A'}
                      </Typography>
                      {agent.cpuUsage && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(agent.cpuUsage, 100)}
                          color={getProgressColor(agent.cpuUsage)}
                          className={classes.progressBar}
                        />
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box className={classes.metricsBox}>
                      <Typography className={classes.metricLabel}>
                        {agent.memoryUsage ? `${agent.memoryUsage.toFixed(1)}%` : 'N/A'}
                      </Typography>
                      {agent.memoryUsage && (
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(agent.memoryUsage, 100)}
                          color={getProgressColor(agent.memoryUsage)}
                          className={classes.progressBar}
                        />
                      )}
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    {agent.cs2Running ? (
                      <Chip label="Running" color="success" size="small" />
                    ) : (
                      <Chip label="Stopped" color="default" size="small" />
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {agent.matchId ? (
                      <Typography 
                        variant="body2" 
                        style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                      >
                        {agent.matchId.substring(0, 8)}...
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    {lastSeenSeconds !== null ? (
                      <Typography variant="body2" color="textSecondary">
                        {lastSeenSeconds < 60 
                          ? `${lastSeenSeconds}s ago`
                          : lastSeenSeconds < 3600
                          ? `${Math.floor(lastSeenSeconds / 60)}m ago`
                          : `${Math.floor(lastSeenSeconds / 3600)}h ago`
                        }
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AgentList;

